import React from 'react';
import type { FC } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { DateTime } from 'luxon';

import { Breadcrumb } from '@shared/components/bootstrap/breadcrumb';
import { Panel } from '@shared/components/bootstrap/panel';

import { Map as CheckinsMap } from '@admin/components/checkins/map';
import { PageHeader } from '@admin/components/helpers/page_header';
import { Permission } from '@admin/components/helpers/permission';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { NotesPanel } from '@admin/components/notes/panel';
import { vehiclesURL } from '@admin/config/routes';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { VEHICLE } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import { History__Subject, NoteNotableEnum, useVehicleDetailsQuery } from '@admin/schema';

import { VehicleStatusLabel } from './status_label';

export const VehicleDetails: FC = () => {
  useTitle('Vehicle');
  const { id } = useParams<{ id: string }>();
  const canReadVehicles = usePermission(VEHICLE.resource, VEHICLE.actions.read);
  const { data } = useVehicleDetailsQuery({
    fetchPolicy: 'cache-and-network',
    client,
    skip: !canReadVehicles,
    variables: { id },
  });

  const vehicle = data?.vehicle;

  return (
    <Permission resource={VEHICLE.resource} action={VEHICLE.actions.read}>
      {!data || !vehicle ? null : (
        <>
          <PageHeader>
            <Breadcrumb>
              <Breadcrumb.Item>
                <NavLink to={vehiclesURL()}>Vehicles</NavLink>
              </Breadcrumb.Item>
              <Breadcrumb.Item active>{vehicle.id}</Breadcrumb.Item>
            </Breadcrumb>
          </PageHeader>

          <Panel>
            <Panel.Header>
              <Panel.Title>Details</Panel.Title>
            </Panel.Header>
            <Panel.Body>
              <p>
                Status: <VehicleStatusLabel vehicle={vehicle} />
              </p>
              <p>Serial Code: {vehicle.serialCode}</p>
              <p>Warehouse: {vehicle.warehouse.name}</p>
              <p>Region: {vehicle.region.name}</p>
              <p>Ownership: {vehicle.ownershipKind ?? '-'}</p>
              <p>Comments: {vehicle.comments ?? '-'}</p>
              <p>Number of Seats: {vehicle.numSeats}</p>
              <p>Cuft: {vehicle.cuft}</p>
              <p>Type: {vehicle.typeName}</p>
              <p>IRM #: {vehicle.irmNumber ?? '-'}</p>
              <p>Provider Unit #: {vehicle.providerUnitNumber ?? '-'}</p>
              <p>EZ Pass / Toll Tag #: {vehicle.tollTagNumber ?? '-'}</p>
              <p>VIN: {vehicle.vin ?? '-'}</p>
              <p>License Plate: {vehicle.licensePlate ?? '-'} </p>
              <p>
                Gross Weight Rating (in Pounds): {vehicle.grossWeightRating ? `${vehicle.grossWeightRating}lbs` : '-'}
              </p>
              <p>Origin State/Province: {vehicle.originState ?? '-'}</p>
              <p>
                Registration Expiration Date:{' '}
                {vehicle.registrationExpiresOn
                  ? DateTime.fromISO(vehicle.registrationExpiresOn).toLocaleString(DateTime.DATE_MED)
                  : '-'}
              </p>
              <p>
                Inspection Expiration Date:{' '}
                {vehicle.inspectionExpiresOn
                  ? DateTime.fromISO(vehicle.inspectionExpiresOn).toLocaleString(DateTime.DATE_MED)
                  : '-'}
              </p>
              <p>
                Preventative Maintenance Due:{' '}
                {vehicle.maintenanceDueOn
                  ? DateTime.fromISO(vehicle.maintenanceDueOn).toLocaleString(DateTime.DATE_MED)
                  : '-'}
              </p>
            </Panel.Body>
          </Panel>

          <CheckinsMap id={id} type="vehicles" />
          <NotesPanel id={id} type={NoteNotableEnum.Vehicle} />
          <HistoriesPanel id={id} type={History__Subject.Vehicle} />
        </>
      )}
    </Permission>
  );
};
